// Utilities and constants for i18n separated to avoid mixing non-component exports
// with React components (helps fast-refresh rules).

export const SUPPORTED_LOCALES = {
  en: { name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇺🇸' },
  sw: { name: 'Swahili', nativeName: 'Kiswahili', dir: 'ltr', flag: '🇰🇪' },
  fr: { name: 'French', nativeName: 'Français', dir: 'ltr', flag: '🇫🇷' },
  es: { name: 'Spanish', nativeName: 'Español', dir: 'ltr', flag: '🇪🇸' },
  pt: { name: 'Portuguese', nativeName: 'Português', dir: 'ltr', flag: '🇧🇷' },
  ar: { name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  zh: { name: 'Chinese', nativeName: '中文', dir: 'ltr', flag: '🇨🇳' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
} as const;

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES;
export type TranslationKey = string;
export type TranslationValues = Record<string, string | number>;

// Translation dictionary type
export interface TranslationDictionary {
  [key: string]: string | TranslationDictionary;
}

// Translation cache and dynamic loader
const translationCache = new Map<SupportedLocale, TranslationDictionary>();

export async function loadTranslations(locale: SupportedLocale): Promise<TranslationDictionary> {
  if (translationCache.has(locale)) return translationCache.get(locale)!;

  try {
    const module = await import(`../locales/${locale}.json`);
    const translations = module?.default ?? {};
    translationCache.set(locale, translations);
    return translations;
  } catch (error) {
    try {
      console.error('RegisterClientLocalizationsError: failed to load locale', {
        locale,
        error: (error as Error).message ?? String(error),
        stack: (error as Error).stack,
      });
    } catch (e) {
      // ignore logging failures
    }

    if (locale !== 'en') {
      return loadTranslations('en');
    }

    return {};
  }
}

// Helpers for translations
export function getNestedValue(obj: TranslationDictionary, path: string): string | undefined {
  const keys = path.split('.');
  let current: TranslationDictionary | string = obj;

  for (const key of keys) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = current[key] as TranslationDictionary | string;
  }

  return typeof current === 'string' ? current : undefined;
}

export function interpolate(template: string, values: TranslationValues): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => values[key]?.toString() ?? match);
}

export function detectBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined') return 'en';
  const browserLocale = navigator.language.split('-')[0];
  if (browserLocale in SUPPORTED_LOCALES) return browserLocale as SupportedLocale;
  return 'en';
}

export function getStoredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('aegis_locale');
  if (stored && stored in SUPPORTED_LOCALES) return stored as SupportedLocale;
  return null;
}

export function storeLocale(locale: SupportedLocale): void {
  if (typeof window !== 'undefined') localStorage.setItem('aegis_locale', locale);
}

export function plural(
  count: number,
  forms: { zero?: string; one: string; few?: string; many?: string; other: string },
  locale: SupportedLocale = 'en',
): string {
  const pluralRules = new Intl.PluralRules(locale);
  const rule = pluralRules.select(count);

  switch (rule) {
    case 'zero':
      return forms.zero ?? forms.other;
    case 'one':
      return forms.one;
    case 'few':
      return forms.few ?? forms.other;
    case 'many':
      return forms.many ?? forms.other;
    default:
      return forms.other;
  }
}

export function getSupportedLocales() {
  return Object.entries(SUPPORTED_LOCALES).map(([code, info]) => ({ code: code as SupportedLocale, ...info }));
}

export function isLocaleSupported(locale: string): locale is SupportedLocale {
  return locale in SUPPORTED_LOCALES;
}

export function getLocaleInfo(locale: SupportedLocale) {
  return SUPPORTED_LOCALES[locale];
}

export default {};
